import {
  put,
  select,
  take,
  delay,
  fork,
  cancel,
  join,
  race,
  cancelled
} from 'redux-saga/effects';
import type { Task, SagaIterator } from 'redux-saga';
import { eventChannel, END, EventChannel, buffers } from 'redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';
import { aiAssistantActions } from './aiAssistantSlice';
import { streamChat } from '../services/streamChat';
import {
  selectConversationId,
  selectRuntimeSessionId,
  selectActiveTool,
  selectExternalContext
} from './selectors';
import type { RoadmapContext } from '../exposes/AIAssistant';

type StreamEvent =
  | { type: 'requestId'; requestId: string }
  | { type: 'messageStart'; messageId: string }
  | { type: 'thinking'; content: string }
  | { type: 'token'; delta: string }
  | { type: 'toolStart'; tool: string }
  | { type: 'done'; conversationId: string; runtimeSessionId: string }
  | { type: 'error'; message: string };

function createStreamChannel(
  message: string,
  conversationId: string | null,
  sessionId: string | null,
  context: RoadmapContext
): EventChannel<StreamEvent> {
  const abortController = new AbortController();

  const channel = eventChannel<StreamEvent>(emitter => {
    streamChat({
      message,
      conversationId,
      sessionId,
      context,
      signal: abortController.signal,
      onRequestId: (requestId: string) => {
        emitter({ type: 'requestId', requestId });
      },
      onMessageStart: (messageId: string) => {
        emitter({ type: 'messageStart', messageId });
      },
      onToken: (delta: string) => {
        emitter({ type: 'token', delta });
      },
      onThinking: (content: string) => {
        emitter({ type: 'thinking', content });
      },
      onToolStart: (tool: string) => {
        emitter({ type: 'toolStart', tool });
      },
      onDone: (convId: string, runtimeSessionId: string) => {
        emitter({ type: 'done', conversationId: convId, runtimeSessionId });
        emitter(END);
      },
      onError: (errorMsg: string) => {
        emitter({ type: 'error', message: errorMsg });
        emitter(END);
      }
    }).catch(err => {
      emitter({ type: 'error', message: err?.message || 'Request failed' });
      emitter(END);
    });

    return () => {
      abortController.abort();
    };
  }, buffers.expanding());

  return channel;
}

function* handleSendMessage(action: PayloadAction<string>): SagaIterator {
  const conversationId: string | null = yield select(selectConversationId);
  const runtimeSessionId: string | null = yield select(selectRuntimeSessionId);
  const externalContext: ReturnType<typeof selectExternalContext> =
    yield select(selectExternalContext);
  const messageId = crypto.randomUUID();

  const channel: EventChannel<StreamEvent> = createStreamChannel(
    action.payload,
    conversationId,
    runtimeSessionId,
    externalContext
  );

  yield put(aiAssistantActions.streamStart({ messageId }));

  let toolClearScheduled = false;

  try {
    while (true) {
      const event: StreamEvent = yield take(channel);
      switch (event.type) {
        case 'requestId':
          yield put(
            aiAssistantActions.setMessageRequestId({
              messageId,
              requestId: event.requestId
            })
          );
          break;
        case 'messageStart':
          break;
        case 'thinking':
          yield put(aiAssistantActions.setThinkingText(event.content));
          break;
        case 'toolStart':
          toolClearScheduled = false;
          yield put(aiAssistantActions.setActiveTool(event.tool));
          break;
        case 'token': {
          if (!toolClearScheduled) {
            const currentTool: string | null = yield select(selectActiveTool);
            if (currentTool !== null) {
              toolClearScheduled = true;
              yield fork(function* () {
                yield delay(1500);
                yield put(aiAssistantActions.setActiveTool(null));
              });
            }
          }
          yield put(
            aiAssistantActions.appendToken({ messageId, token: event.delta })
          );
          // Skip the yield when the tab is hidden — setTimeout is throttled to
          // ~1 000 ms per tick in background tabs, which stalls long responses.
          if (!document.hidden) {
            yield delay(0);
          }
          break;
        }
        case 'done':
          yield put(aiAssistantActions.setConversationId(event.conversationId));
          if (event.runtimeSessionId) {
            yield put(
              aiAssistantActions.setRuntimeSessionId(event.runtimeSessionId)
            );
          }
          yield put(aiAssistantActions.streamComplete(messageId));
          break;
        case 'error':
          yield put(aiAssistantActions.streamError(event.message));
          break;
      }
    }
  } finally {
    channel.close();
    if (yield cancelled()) {
      yield put(aiAssistantActions.streamComplete(messageId));
    }
  }
}

export function* aiAssistantSaga(): SagaIterator {
  let pendingAction: PayloadAction<string> | null = null;

  while (true) {
    const sendAction: PayloadAction<string> =
      pendingAction != null
        ? pendingAction
        : ((yield take(
            aiAssistantActions.sendMessage.type
          )) as PayloadAction<string>);
    pendingAction = null;

    const task = (yield fork(handleSendMessage, sendAction)) as Task;

    const { interrupt } = (yield race({
      done: join(task),
      interrupt: take([
        aiAssistantActions.cancelStream.type,
        aiAssistantActions.sendMessage.type
      ])
    })) as { done?: unknown; interrupt?: PayloadAction };

    if (interrupt) {
      yield cancel(task);
      if (interrupt.type === aiAssistantActions.sendMessage.type) {
        pendingAction = interrupt as unknown as PayloadAction<string>;
      }
    }
  }
}
